import React, { useContext, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ESInput from "../../controls/ESInput";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import {
  getUserDesignationById,
  insertUserDesignation,
  updateUserDesignation,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  checkIfSuperAdmin,
  getCachedData,
  Option,
  queryClient
} from "../../utils/common";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import Loader from "../loader/Loader";
import "./UserDesignationList.scss";

interface UserDesignation {
  DesignationID: number;
  DesignationName: string;
  DesignationDescription: string;
  IsActive: number;
  CreateUserId: number;
  UpdateUserId: number;
  CreateDate: string;
  UpdateDate: string;
  InternalKey: string;
  ConcurrencyKey: string;
  OrganizationId: number;
}

interface AddUserDesignationProps {
  designationId?: number | null;
  onClose: () => void;
}

const AddUserDesignation: React.FC<AddUserDesignationProps> = ({
  designationId,
  onClose,
}) => {
  const methods = useForm<UserDesignation>({
    defaultValues: {
      DesignationName: "",
      IsActive: 1,
    },
  });
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<number>(0);
  const { appContextdata } = useContext<any>(UserContext);

  const fetchDesignationDetailsById = async (designationId: number) => {
    setLoading(true);
    axiosPost(getUserDesignationById, { DesignationID: designationId })
      .then((response: any) => {
        if (response?.data?.Status === 200) {
          const data = response.data.ExtraData;
          const transformedData = {
            ...data,
            OrganizationId: organizations?.find(
              (org: Option) => org.value === data.OrganizationId
            )
              ? {
                  label: organizations?.find(
                    (org: Option) => org.value === data.OrganizationId
                  )?.label,
                  value: data.OrganizationId,
                }
              : null,
          };
          methods.reset(transformedData);
          setSelectedOrganization(data.OrganizationId);
        } else {
          showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
        }
      })
      .catch((error) => {
        showToast("Error fetching designation details", ToastTypeCode?.ERROR);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        if (designationId && organizations?.length >= 1) {
          fetchDesignationDetailsById(designationId);
        }
      } else {
        if (designationId) {
          fetchDesignationDetailsById(designationId);
        }
      }
    }
  }, [appContextdata?.Role, designationId, organizations, methods]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData('organizations')) as Option[];
        setOrganizations(options);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const handleOrganizationChange = async (selectedOption: any) => {
    const organizationId = selectedOption?.value;
    setSelectedOrganization(organizationId);
  };

  const onSubmit: SubmitHandler<UserDesignation> = async (data) => {
    const transformedData = {
      ...data,
      IsActive: data.IsActive ? 1 : 0,
      OrganizationId: selectedOrganization
        ? selectedOrganization
        : appContextdata?.Organization,
        CreateDate:new Date().toISOString()
    };
    setLoading(true);
    try {
      const response: any = designationId
        ? await axiosPost(updateUserDesignation, transformedData)
        : await axiosPost(insertUserDesignation, transformedData);

      if (response?.data?.Status === 200) {
      queryClient.invalidateQueries("designation");
        const message = designationId
          ? "Designation updated successfully"
          : "Designation created successfully";
        showToast(message, ToastTypeCode?.SUCCESS);
        onClose();
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error saving designation", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sub-card">
      <div>
        {loading ? (
          <Loader />
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Row>
                {isSuperAdmin && (
                  <Form.Group as={Col} md="3">
                    <Form.Label>
                      Organization <span className="text-danger">*</span>
                    </Form.Label>
                    <ESSearchDropdown
                      name="OrganizationId"
                      placeholder="Organization"
                      options={organizations}
                      onChange={handleOrganizationChange}
                    />
                  </Form.Group>
                )}
                <Form.Group as={Col} md="3">
                  <Form.Label>
                    Designation Name
                    <span className="text-danger">*</span>
                  </Form.Label>

                  <ESInput
                    className="form-control"
                    name="DesignationName"
                    placeholder="Designation name"
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                  />
                </Form.Group>
                <Form.Group as={Col} md="3">
                  <Form.Label>
                    Description
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <ESInput
                    className="form-control"
                    name="DesignationDescription"
                    placeholder="Description"
                    type="text"
                    required
                    minLength={2}
                    maxLength={200}
                  />
                </Form.Group>
                <Col md="2">
                  <div className="form-check">
                    <ESInput name="IsActive" type="checkbox" placeholder={""} className="form-check-input" />
                    <label htmlFor="IsActive" className="form-check-label">Is Active</label>
                  </div>
                </Col>
                <Col md="4">
                  <button type="submit" className="save-btn">
                  {designationId ? "Update" : "Save"}
                  </button>
                  <button type="button" className="back-btn" onClick={onClose}>
                    Cancel
                  </button>
                </Col>
              </Row>
            </form>
          </FormProvider>
        )}
      </div>
    </div>
  );
};

export default AddUserDesignation;
